/**
 * Admin Templates Screen
 *
 * Only visible to users with admin custom claim. Create and list trip templates.
 * Shows a "Pending review" backlog for community-submitted templates (approve/reject).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  ScrollView,
  RefreshControl,
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Alert,
  Modal,
  TextInput,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Screen } from '@/components/layout/Screen';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Body, Caption, SubHeading } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { fetchTripTemplates, fetchPendingTemplates, getTemplateById } from '@/services/tripTemplates';
import {
  adminDeleteTripTemplate,
  adminApproveTripTemplate,
  adminRejectTripTemplate,
} from '@/services/adminTemplates';
import { TemplateBuilder } from '@/components/templates/TemplateBuilder';
import type { TripTemplate } from '@/types/tripTemplates';
import { TripErrorBoundary } from '@/utils/errorBoundary';
import {
  cloneTemplateAsNewDraft,
  getTemplateDisplayStatus,
  type TemplateBuilderDraft,
} from '@/utils/templateBuilderDraft';

function AdminTemplatesScreenInner() {
  const theme = useTheme();
  const router = useRouter();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const [templates, setTemplates] = useState<TripTemplate[]>([]);
  const [pendingTemplates, setPendingTemplates] = useState<TripTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPending, setLoadingPending] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TripTemplate | null>(null);
  const [builderSeedDraft, setBuilderSeedDraft] = useState<Partial<TemplateBuilderDraft> | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTemplate, setRejectTemplate] = useState<TripTemplate | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [loadingEditId, setLoadingEditId] = useState<string | null>(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const list = await fetchTripTemplates(false);
      setTemplates(list);
    } catch (error) {
      console.error('[AdminTemplates] Error loading templates:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadPendingTemplates = useCallback(async () => {
    try {
      setLoadingPending(true);
      const list = await fetchPendingTemplates();
      setPendingTemplates(list);
    } catch (error) {
      console.error('[AdminTemplates] Error loading pending templates:', error);
    } finally {
      setLoadingPending(false);
    }
  }, []);

  const loadAll = useCallback(() => {
    loadTemplates();
    loadPendingTemplates();
  }, [loadTemplates, loadPendingTemplates]);

  useEffect(() => {
    if (!isAdmin && !adminLoading) {
      router.replace('/(tabs)/profile');
      return;
    }
    if (isAdmin) {
      loadAll();
    }
  }, [isAdmin, adminLoading, loadAll, router]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    loadAll();
  }, [loadAll]);

  const handleSuccess = useCallback((templateId: string) => {
    setShowBuilder(false);
    setEditingTemplate(null);
    setBuilderSeedDraft(null);
    loadAll();
  }, [loadAll]);

  const handleDraftSaved = useCallback(
    async (templateId: string) => {
      try {
        const fetched = await getTemplateById(templateId);
        if (fetched) {
          setEditingTemplate(fetched);
          setBuilderSeedDraft(null);
        }
        loadAll();
      } catch (err) {
        console.error('[AdminTemplates] Error refreshing draft template:', err);
        loadAll();
      }
    },
    [loadAll],
  );

  const openBuilderNew = useCallback(() => {
    setEditingTemplate(null);
    setBuilderSeedDraft(null);
    setShowBuilder(true);
  }, []);

  const openBuilderEdit = useCallback(async (t: TripTemplate) => {
    setLoadingEditId(t.id);
    try {
      const fetched = await getTemplateById(t.id);
      setEditingTemplate(fetched ?? t);
      setBuilderSeedDraft(null);
      setShowBuilder(true);
    } catch (err) {
      console.error('[AdminTemplates] Error loading template for edit:', err);
      setEditingTemplate(t);
      setBuilderSeedDraft(null);
      setShowBuilder(true);
    } finally {
      setLoadingEditId(null);
    }
  }, []);

  const openBuilderDuplicate = useCallback(
    async (t: TripTemplate) => {
      setDuplicatingId(t.id);
      try {
        const fetched = await getTemplateById(t.id);
        const source = fetched ?? t;
        setEditingTemplate(null);
        setBuilderSeedDraft(cloneTemplateAsNewDraft(source));
        setShowBuilder(true);
      } catch (err) {
        console.error('[AdminTemplates] Error loading template for duplicate:', err);
        Alert.alert('Error', 'Could not load template to duplicate.');
      } finally {
        setDuplicatingId(null);
      }
    },
    [],
  );

  const handleApprove = useCallback(
    async (t: TripTemplate) => {
      setApprovingId(t.id);
      try {
        await adminApproveTripTemplate(t.id);
        loadAll();
      } catch (err) {
        console.error('[AdminTemplates] Approve failed:', err);
        Alert.alert(
          'Error',
          err instanceof Error ? err.message : 'Failed to approve template.'
        );
      } finally {
        setApprovingId(null);
      }
    },
    [loadAll]
  );

  const openRejectModal = useCallback((t: TripTemplate) => {
    setRejectTemplate(t);
    setRejectReason('');
    setShowRejectModal(true);
  }, []);

  const handleRejectSubmit = useCallback(async () => {
    const t = rejectTemplate;
    if (!t || !rejectReason.trim()) {
      Alert.alert('Reason required', 'Please enter a reason for rejection (shown to the creator).');
      return;
    }
    setRejectSubmitting(true);
    try {
      await adminRejectTripTemplate(t.id, rejectReason.trim());
      setShowRejectModal(false);
      setRejectTemplate(null);
      setRejectReason('');
      loadAll();
    } catch (err) {
      console.error('[AdminTemplates] Reject failed:', err);
      Alert.alert(
        'Error',
        err instanceof Error ? err.message : 'Failed to reject template.'
      );
    } finally {
      setRejectSubmitting(false);
    }
  }, [rejectTemplate, rejectReason, loadAll]);

  if (adminLoading || (!isAdmin && !adminLoading)) {
    return (
      <Screen>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.accent} />
        </View>
      </Screen>
    );
  }

  return (
    <Screen
      title="Template manager"
      subtitle="Create and manage trip templates"
      headerAction={
        <Pressable
          onPress={() => router.push('/(tabs)/profile')}
          style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
        >
          <Ionicons name="close" size={24} color={theme.colors.text} />
        </Pressable>
      }
    >
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        <Card>
          <Button
            label="Create template"
            variant="primary"
            onPress={openBuilderNew}
            icon={<Ionicons name="add" size={20} color={theme.colors.surface} />}
          />
        </Card>

        {/* Pending review backlog (community submissions) */}
        <View style={styles.pendingSection}>
          <SubHeading style={styles.listTitle}>
            Pending review {pendingTemplates.length > 0 ? `(${pendingTemplates.length})` : ''}
          </SubHeading>
          {loadingPending ? (
            <View style={styles.loadingRow}>
              <ActivityIndicator size="small" color={theme.colors.primary} />
              <Caption color={theme.colors.textSecondary}>Loading pending…</Caption>
            </View>
          ) : pendingTemplates.length === 0 ? (
            <Body color={theme.colors.textSecondary}>
              No templates waiting for review. Community submissions will appear here.
            </Body>
          ) : (
            pendingTemplates.map((t) => (
              <Card key={t.id} variant="outlined" style={styles.pendingCard}>
                <View style={styles.pendingInfo}>
                  <Body style={styles.templateTitle}>{t.title}</Body>
                  <Caption color={theme.colors.textSecondary}>
                    {t.destination} · {t.duration} days
                    {t.creatorName ? ` · by ${t.creatorName}` : ''}
                  </Caption>
                </View>
                <View style={styles.pendingActions}>
                  <Pressable
                    onPress={() => handleApprove(t)}
                    disabled={approvingId === t.id}
                    style={({ pressed }) => [
                      styles.approveButton,
                      pressed && { opacity: 0.7 },
                      approvingId === t.id && { opacity: 0.6 },
                    ]}
                  >
                    {approvingId === t.id ? (
                      <ActivityIndicator size="small" color={theme.colors.primary} />
                    ) : (
                      <>
                        <Ionicons name="checkmark-circle-outline" size={20} color={theme.colors.primary} />
                        <Body style={[styles.actionLabel, { color: theme.colors.primary }]}>Approve</Body>
                      </>
                    )}
                  </Pressable>
                  <Pressable
                    onPress={() => openRejectModal(t)}
                    style={({ pressed }) => [styles.rejectButton, pressed && { opacity: 0.7 }]}
                  >
                    <Ionicons name="close-circle-outline" size={20} color={theme.colors.error ?? '#dc3545'} />
                    <Body style={[styles.actionLabel, { color: theme.colors.error ?? '#dc3545' }]}>Reject</Body>
                  </Pressable>
                </View>
              </Card>
            ))
          )}
        </View>

        {loading ? (
          <View style={styles.loadingRow}>
            <ActivityIndicator size="small" color={theme.colors.primary} />
            <Caption color={theme.colors.textSecondary}>Loading templates…</Caption>
          </View>
        ) : (
          <View style={styles.list}>
            <SubHeading style={styles.listTitle}>Templates ({templates.length})</SubHeading>
            {templates.length === 0 ? (
              <Body color={theme.colors.textSecondary}>No templates yet. Create one above.</Body>
            ) : (
              templates.map((t) => {
                const displayStatus = getTemplateDisplayStatus(t);
                return (
                <Card key={t.id} variant="outlined" style={styles.templateCard}>
                  <Pressable
                    style={({ pressed }) => [styles.templateRow, pressed && styles.templateRowPressed]}
                    onPress={() => openBuilderEdit(t)}
                    disabled={loadingEditId === t.id || duplicatingId === t.id}
                  >
                    <View style={styles.templateInfo}>
                      <View style={styles.templateTitleRow}>
                        <Body style={styles.templateTitle}>{t.title}</Body>
                        {displayStatus === 'draft' && (
                          <View style={[styles.statusBadge, { backgroundColor: `${theme.colors.warning}22` }]}>
                            <Caption style={{ color: theme.colors.warning, fontWeight: '700', fontSize: 11 }}>
                              Draft
                            </Caption>
                          </View>
                        )}
                      </View>
                      <Caption color={theme.colors.textSecondary}>
                        {t.destination} · {t.duration} days
                      </Caption>
                    </View>
                    <View style={styles.templateActions}>
                      {loadingEditId === t.id || duplicatingId === t.id ? (
                        <ActivityIndicator size="small" color={theme.colors.primary} style={styles.editSpinner} />
                      ) : null}
                      <Pressable
                        onPress={(e) => {
                          e.stopPropagation();
                          openBuilderDuplicate(t);
                        }}
                        style={({ pressed }) => [styles.iconActionButton, pressed && { opacity: 0.7 }]}
                        hitSlop={8}
                        accessibilityRole="button"
                        accessibilityLabel={`Duplicate ${t.title}`}
                      >
                        <Ionicons name="copy-outline" size={22} color={theme.colors.primary} />
                      </Pressable>
                      <Pressable
                        onPress={(e) => {
                          e.stopPropagation();
                          Alert.alert(
                            'Delete template',
                            `Delete "${t.title}"? This cannot be undone.`,
                            [
                              { text: 'Cancel', style: 'cancel' },
                              {
                                text: 'Delete',
                                style: 'destructive',
                                onPress: async () => {
                                  try {
                                    await adminDeleteTripTemplate(t.id);
                                    loadAll();
                                  } catch (err) {
                                    console.error('[AdminTemplates] Delete failed:', err);
                                    Alert.alert(
                                      'Error',
                                      err instanceof Error ? err.message : 'Failed to delete template.'
                                    );
                                  }
                                },
                              },
                            ]
                          );
                        }}
                        style={({ pressed }) => [styles.deleteButton, pressed && { opacity: 0.7 }]}
                        hitSlop={8}
                      >
                        <Ionicons name="trash-outline" size={22} color={theme.colors.error ?? '#dc3545'} />
                      </Pressable>
                      <Ionicons name="chevron-forward" size={22} color={theme.colors.textSecondary} />
                    </View>
                  </Pressable>
                </Card>
              );
              })
            )}
          </View>
        )}
      </ScrollView>

      <TemplateBuilder
        visible={showBuilder}
        onClose={() => {
          setShowBuilder(false);
          setEditingTemplate(null);
          setBuilderSeedDraft(null);
        }}
        onSuccess={handleSuccess}
        onDraftSaved={handleDraftSaved}
        sourceTripId={undefined}
        existingTemplate={editingTemplate}
        seedDraft={builderSeedDraft}
        isAdminMode
      />

      {/* Reject reason modal (cross-platform) */}
      <Modal
        visible={showRejectModal}
        transparent
        animationType="fade"
        onRequestClose={() => !rejectSubmitting && setShowRejectModal(false)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => !rejectSubmitting && setShowRejectModal(false)}
        >
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}
            style={styles.modalContentWrap}
          >
            <Pressable onPress={(e) => e.stopPropagation()} style={[styles.rejectModalCard, { backgroundColor: theme.colors.surface }]}>
              <SubHeading style={styles.rejectModalTitle}>Reject template</SubHeading>
              {rejectTemplate && (
                <Caption color={theme.colors.textSecondary} style={styles.rejectModalTemplate}>
                  {rejectTemplate.title}
                </Caption>
              )}
              <Body style={styles.rejectModalLabel}>Reason for rejection (shown to creator)</Body>
              <TextInput
                value={rejectReason}
                onChangeText={setRejectReason}
                placeholder="e.g. Insufficient itinerary detail, needs more activities per day"
                placeholderTextColor={theme.colors.textSecondary}
                multiline
                numberOfLines={3}
                style={[
                  styles.rejectInput,
                  {
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text,
                    borderColor: theme.colors.border,
                  },
                ]}
                editable={!rejectSubmitting}
              />
              <View style={styles.rejectModalActions}>
                <Button
                  label="Cancel"
                  variant="secondary"
                  onPress={() => {
                    if (!rejectSubmitting) {
                      setShowRejectModal(false);
                      setRejectTemplate(null);
                      setRejectReason('');
                    }
                  }}
                  disabled={rejectSubmitting}
                />
                <Button
                  label={rejectSubmitting ? 'Rejecting…' : 'Reject'}
                  variant="primary"
                  onPress={handleRejectSubmit}
                  disabled={rejectSubmitting || !rejectReason.trim()}
                />
              </View>
            </Pressable>
          </KeyboardAvoidingView>
        </Pressable>
      </Modal>
    </Screen>
  );
}

export default function AdminTemplatesScreen() {
  return (
    <TripErrorBoundary name="GamificationAdminTemplates">
      <AdminTemplatesScreenInner />
    </TripErrorBoundary>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: { padding: 16, paddingBottom: 32 },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 16,
  },
  pendingSection: { marginTop: 16 },
  pendingCard: { marginBottom: 8 },
  pendingInfo: { marginBottom: 8 },
  pendingActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  approveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  rejectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  actionLabel: { fontSize: 14, fontWeight: '600' },
  list: { marginTop: 16 },
  listTitle: { marginBottom: 12 },
  templateCard: { marginBottom: 8 },
  templateRow: { flexDirection: 'row', alignItems: 'center' },
  templateRowPressed: { opacity: 0.7 },
  templateInfo: { flex: 1, minWidth: 0 },
  templateTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 2,
  },
  templateTitle: { fontWeight: '600', flexShrink: 1 },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
  },
  templateActions: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  iconActionButton: { padding: 8 },
  deleteButton: { padding: 8 },
  editSpinner: { marginRight: 4 },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalContentWrap: { width: '100%', maxWidth: 400 },
  rejectModalCard: {
    borderRadius: 12,
    padding: 20,
  },
  rejectModalTitle: { marginBottom: 4 },
  rejectModalTemplate: { marginBottom: 16 },
  rejectModalLabel: { marginBottom: 8 },
  rejectInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    minHeight: 80,
    textAlignVertical: 'top',
    marginBottom: 16,
  },
  rejectModalActions: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'flex-end',
  },
});
